import { type components, useApi } from "@appretio/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type GridKey = components["schemas"]["GridKey"];
export type GridView = components["schemas"]["GridView"];
export type GridViewCreate = components["schemas"]["GridViewCreate"];
export type GridViewUpdate = components["schemas"]["GridViewUpdate"];

export const gridViewQueryKeys = {
  all: ["gridViews"] as const,
  list: (grid: GridKey) => [...gridViewQueryKeys.all, grid] as const
} as const;

/** The user's saved views for one grid, name-ordered by the backend. */
export function useFetchGridViews(grid: GridKey) {
  const apiClient = useApi();

  return useQuery({
    queryKey: gridViewQueryKeys.list(grid),
    meta: { errorMessage: "Failed to fetch saved views" },
    queryFn: async () => {
      const response = await apiClient.POST("/v2/grid-views/get-grid-views", {
        body: { grid }
      });
      return response.data?.result ?? [];
    }
  });
}

/**
 * A write to the user's views for one grid; it then refetches that grid's
 * list so the picker shows the result at once.
 *
 * ``errorMessage`` is optional on purpose: the save and edit dialogs surface
 * failures inline (a 409 on the name field, the rest as a form error), so
 * they pass none and the app-level toast stays quiet.
 */
function useGridViewMutation<TVariables, TResult>(
  grid: GridKey,
  mutationFn: (
    apiClient: ReturnType<typeof useApi>,
    variables: TVariables
  ) => Promise<TResult>,
  errorMessage?: string
) {
  const apiClient = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: TVariables) => mutationFn(apiClient, variables),
    // On failure too: a 404 means the view was deleted elsewhere, so the
    // picker is showing a row that no longer exists.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: gridViewQueryKeys.list(grid)
      });
    },
    meta: errorMessage ? { errorMessage } : undefined
  });
}

/** Save a named view; not the grid's default unless the body says so. */
export function useSaveGridView(grid: GridKey) {
  return useGridViewMutation(
    grid,
    async (
      apiClient,
      body: Omit<GridViewCreate, "grid" | "is_default"> &
        Partial<Pick<GridViewCreate, "is_default">>
    ) => {
      const response = await apiClient.POST("/v2/grid-views/save-grid-view", {
        body: { is_default: false, ...body, grid }
      });
      return response.data?.result;
    }
  );
}

/** Rename a view, replace its state, or both. */
export function useUpdateGridView(grid: GridKey, errorMessage?: string) {
  return useGridViewMutation(
    grid,
    async (apiClient, body: GridViewUpdate) => {
      const response = await apiClient.POST("/v2/grid-views/update-grid-view", {
        body
      });
      return response.data?.result;
    },
    errorMessage
  );
}

/** Delete one of the user's saved views. */
export function useDeleteGridView(grid: GridKey) {
  return useGridViewMutation(
    grid,
    async (apiClient, viewId: string) => {
      const response = await apiClient.POST("/v2/grid-views/delete-grid-view", {
        body: { view_id: viewId }
      });
      return response.data?.result ?? false;
    },
    "Failed to delete saved view"
  );
}

/** Make one of the user's views the one this grid opens on. */
export function useSetDefaultGridView(grid: GridKey) {
  return useGridViewMutation(
    grid,
    async (apiClient, viewId: string) => {
      const response = await apiClient.POST(
        "/v2/grid-views/set-default-grid-view",
        { body: { view_id: viewId } }
      );
      return response.data?.result;
    },
    "Failed to set the default view"
  );
}
